import React, { useState, useEffect } from "react";
import moment from "moment";
import { Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ClockCircleOutlined,
  CloseOutlined,
  CheckOutlined,
  DeleteFilled,
} from "@ant-design/icons";
import { getApi, patchApi, deleteApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";

const NotificationList = ({ onClose, unreadCount: propUnreadCount, setUnreadCount: propSetUnreadCount }) => {
  const navigate = useNavigate();
  const [notificationsData, setNotificationsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [localUnreadCount, setLocalUnreadCount] = useState(0);

  const unreadCount = propUnreadCount !== undefined ? propUnreadCount : localUnreadCount;
  const setUnreadCount = (val) => {
    if (propSetUnreadCount) {
      propSetUnreadCount(val);
    }
    setLocalUnreadCount(val);
  };

  useEffect(() => {
    fetchData();
    // Disable body scroll when open
    document.body.classList.add("overflow-hidden");
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_NOTIFICATIONS(1, 100), // fetch recent 100
      });
      if (response && response.success) {
        setNotificationsData(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await patchApi({
        apiUrl: API_URLS.MARK_ALL_NOTIFICATIONS_READ,
      });
      if (response && response.success) {
        setNotificationsData((prev) =>
          prev.map((n) => ({ ...n, isRead: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleMarkAsRead = async (id, e) => {
    if (e) e.stopPropagation(); // prevent card click redirect
    try {
      const response = await patchApi({
        apiUrl: API_URLS.MARK_NOTIFICATION_READ(id),
      });
      if (response && response.success) {
        setNotificationsData((prev) =>
          prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking single notification as read:", error);
    }
  };

  const handleDelete = async (id, isUnread, e) => {
    if (e) e.stopPropagation(); // prevent card click redirect
    try {
      const response = await deleteApi({
        apiUrl: API_URLS.DELETE_NOTIFICATION(id),
      });
      if (response && response.success) {
        setNotificationsData((prev) => prev.filter((n) => n._id !== id));
        if (isUnread) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleCardClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }
    if (notification.metadata?.actionUrl) {
      navigate(notification.metadata.actionUrl);
      if (onClose) onClose();
    }
  };

  const formatDateOnly = (createdAt) => {
    if (!createdAt) return "";
    return moment(createdAt).format("MMMM DD, YYYY");
  };

  const formatTimeOnly = (createdAt) => {
    if (!createdAt) return "";
    return moment(createdAt)
      .format("hh:mm A")
      .replace("AM", "A.M.")
      .replace("PM", "P.M.");
  };

  const groupNotifications = (notifications) => {
    const groups = {
      Today: [],
      Yesterday: [],
      Older: [],
    };
    notifications.forEach((n) => {
      const date = moment(n.createdAt);
      if (date.isSame(moment(), "day")) {
        groups.Today.push(n);
      } else if (date.isSame(moment().subtract(1, "day"), "day")) {
        groups.Yesterday.push(n);
      } else {
        groups.Older.push(n);
      }
    });
    return groups;
  };

  const grouped = groupNotifications(notificationsData);

  return (
    <>
      <div className="bg-white rounded-2xl w-[550px] shadow-2xl border border-gray-100 flex flex-col max-h-[600px] overflow-hidden font-sans">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900">Your Notifications</span>
            {unreadCount > 0 && (
              <span className="bg-[#0B5C45] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleMarkAllAsRead}
              className="bg-[#0B5C45] hover:bg-[#084533] text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
            >
              <CheckOutlined className="text-xs" />
              Mark all as read
            </button>
            <CloseOutlined
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer text-lg p-1"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="px-6 py-4 overflow-y-auto flex-grow custom-scrollbar">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton active paragraph={{ rows: 2 }} />
              <Skeleton active paragraph={{ rows: 2 }} />
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ) : notificationsData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-[#F2FAF7] rounded-full flex items-center justify-center mb-4">
                <CheckOutlined className="text-[#0B5C45] text-2xl" />
              </div>
              <p className="text-gray-900 font-bold text-base">All caught up!</p>
              <p className="text-gray-400 text-xs mt-1">No notifications found yet.</p>
            </div>
          ) : (
            ["Today", "Yesterday", "Older"].map((category) => {
              const list = grouped[category];
              if (list.length === 0) return null;

              return (
                <div key={category} className="mb-6 last:mb-2">
                  <h3 className="text-gray-400 font-semibold text-xs uppercase tracking-wider mb-3">
                    {category}
                  </h3>
                  <div className="space-y-3">
                    {list.map((notification) => (
                      <div
                        key={notification._id}
                        onClick={() => handleCardClick(notification)}
                        className={`flex border rounded-xl overflow-hidden relative shadow-sm border-l-[6px] border-l-[#0A5C47] cursor-pointer hover:shadow-md transition-all ${
                          notification.isRead
                            ? "bg-white border-gray-200"
                            : "bg-[#F2FAF7] border-[#D4ECE6]"
                        }`}
                      >
                        <div className="p-4 flex-grow flex justify-between items-start gap-4">
                          {/* Left Details */}
                          <div className="flex-grow">
                            <h4 className="font-bold text-[#0A5C47] text-base leading-snug">
                              {notification.title}
                            </h4>
                            <p className="text-gray-600 text-xs mt-2 leading-relaxed font-normal">
                              {notification.message}
                            </p>
                          </div>

                          {/* Right Metadata and Actions */}
                          <div className="flex flex-shrink-0 flex-col items-end justify-between min-h-[80px] text-right">
                            {/* Date / Time */}
                            <div className="text-gray-400 text-xs font-normal leading-normal whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1">
                                <ClockCircleOutlined className="text-[10px]" />
                                <span>{formatDateOnly(notification.createdAt)} |</span>
                              </div>
                              <div className="pr-1">{formatTimeOnly(notification.createdAt)}</div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-3 mt-auto">
                              {!notification.isRead && (
                                <button
                                  onClick={(e) => handleMarkAsRead(notification._id, e)}
                                  className="text-[#0A5C47] hover:text-[#074534] hover:underline font-bold text-xs cursor-pointer transition-colors"
                                >
                                  Mark as read
                                </button>
                              )}
                              <button
                                onClick={(e) => handleDelete(notification._id, !notification.isRead, e)}
                                className="hover:scale-110 transition-transform p-0.5"
                                title="Delete notification"
                              >
                                <DeleteFilled style={{ color: "#FF3B30", fontSize: "18px" }} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #0b5c45;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #084533;
        }
      `}</style>
    </>
  );
};

export default NotificationList;
